import React,{createContext,useContext,useState,useCallback,useRef,useEffect} from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {motion,AnimatePresence} from 'framer-motion';

const UIContext=createContext();

function PromptTextModal({options,onConfirm,onCancel}) {
  const [value,setValue]=useState(options.initialValue || '');
  const [error,setError]=useState('');
  const [hasInteracted,setHasInteracted]=useState(false);
  const [confirming,setConfirming]=useState(false);
  const inputRef=useRef(null);

  useEffect(()=> {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  },[]);

  useEffect(()=> {
    const handleKeyDown=(e)=> {
      if (e.key==='Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown',handleKeyDown);
    return ()=> window.removeEventListener('keydown',handleKeyDown);
  },[onCancel]);

  const runValidate=(val)=> {
    const trimmed=val.trim();
    if (options.validate) {
      return options.validate(trimmed);
    }
    if (!trimmed) return 'This field is required.';
    return '';
  };

  const handleChange=(e)=> {
    const newValue=e.target.value;
    setValue(newValue);
    if (hasInteracted) {
      setError(runValidate(newValue));
    }
  };

  const handleConfirm=()=> {
    if (confirming) return;
    setHasInteracted(true);
    const trimmed=value.trim();
    const err=runValidate(value);
    if (err) {
      setError(err);
      return;
    }
    setConfirming(true);
    onConfirm(trimmed);
  };

  const handleKeyDown=(e)=> {
    if (e.key==='Enter') {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <motion.div
        initial={{scale: 0.95,opacity: 0}}
        animate={{scale: 1,opacity: 1}}
        exit={{scale: 0.95,opacity: 0}}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        <div className="p-6 overflow-y-auto">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 bg-indigo-100 text-indigo-600">
            <SafeIcon icon={FiIcons.FiFileText} className="text-2xl" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{options.title}</h3>
          {options.message && (
            <p className="text-gray-600 mb-4 text-sm leading-relaxed">{options.message}</p>
          )}
          <div>
            {options.label && (
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {options.label}
              </label>
            )}
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              maxLength={options.maxLength || undefined}
              placeholder={options.placeholder || ''}
              className={`w-full border rounded-lg px-4 py-2.5 focus:ring-2 outline-none transition-all text-sm font-medium ${
                error ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
              }`}
            />
            {options.helperText && !error && (
              <p className="text-xs text-gray-400 mt-1.5 font-medium">{options.helperText}</p>
            )}
            {error && (
              <p className="text-xs text-red-600 mt-1.5 font-medium">{error}</p>
            )}
          </div>
        </div>
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 shrink-0 border-t border-gray-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
          >
            {options.cancelText || 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirming}
            className="px-6 py-2 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {options.confirmText || 'Confirm'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export const UIProvider=({children})=> {
  const [modal,setModal]=useState(null);
  const [promptModal,setPromptModal]=useState(null);
  const [toasts,setToasts]=useState([]);

  const showToast=useCallback((message,type='success')=> {
    const id=crypto.randomUUID();
    setToasts(prev=> [...prev,{id,message,type}]);
    setTimeout(()=> {
      setToasts(prev=> prev.filter(t=> t.id !==id));
    },4000);
  },[]);

  const confirm=useCallback((options)=> {
    return new Promise((resolve)=> {
      setModal({...options,resolve});
    });
  },[]);

  const promptText=useCallback((options)=> {
    return new Promise((resolve)=> {
      setPromptModal({...options,resolve});
    });
  },[]);

  const closeModal=()=> {
    if (modal) {
      modal.resolve(false);
      setModal(null);
    }
  };

  const handleConfirm=()=> {
    if (modal) {
      modal.resolve(true);
      setModal(null);
    }
  };

  const closePrompt=()=> {
    if (promptModal) {
      promptModal.resolve(null);
      setPromptModal(null);
    }
  };

  const handlePromptConfirm=(value)=> {
    if (promptModal) {
      promptModal.resolve(value);
      setPromptModal(null);
    }
  };

  return (
    <UIContext.Provider value={{showToast,confirm,promptText}}>
      {children}
      {/* Custom Modal — responsive with internal scroll */}
      <AnimatePresence>
        {modal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} onClick={closeModal} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
            <motion.div initial={{scale: 0.95,opacity: 0}} animate={{scale: 1,opacity: 1}} exit={{scale: 0.95,opacity: 0}} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85dvh] flex flex-col overflow-hidden" >
              <div className="p-6 overflow-y-auto">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${modal.type==='danger' ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`} >
                  <SafeIcon icon={modal.type==='danger' ? FiIcons.FiAlertTriangle : FiIcons.FiHelpCircle} className="text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{modal.title}</h3>
                <p className="text-gray-600">{modal.message}</p>
              </div>
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 shrink-0 border-t border-gray-100">
                <button onClick={closeModal} className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors" >
                  {modal.cancelText || 'Cancel'}
                </button>
                <button onClick={handleConfirm} className={`px-6 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all ${modal.type==='danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`} >
                  {modal.confirmText || 'Confirm'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Prompt Text Modal */}
      <AnimatePresence>
        {promptModal && (
          <PromptTextModal
            options={promptModal}
            onConfirm={handlePromptConfirm}
            onCancel={closePrompt}
          />
        )}
      </AnimatePresence>
      {/* Toast Notifications — responsive width */}
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[110] flex flex-col gap-3 items-stretch sm:items-end">
        <AnimatePresence>
          {toasts.map(toast=> (
            <motion.div key={toast.id} initial={{x: 20,opacity: 0}} animate={{x: 0,opacity: 1}} exit={{x: 20,opacity: 0}} className={`flex items-center gap-3 px-5 py-4 rounded-xl shadow-lg border w-full sm:min-w-[300px] sm:max-w-[420px] ${toast.type==='error' ? 'bg-red-50 border-red-100 text-red-800' : 'bg-white border-gray-100 text-gray-800'}`} >
              <SafeIcon icon={toast.type==='error' ? FiIcons.FiXCircle : FiIcons.FiCheckCircle} className={toast.type==='error' ? 'text-red-500 shrink-0' : 'text-green-500 shrink-0'} />
              <span className="font-semibold text-sm">{toast.message}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </UIContext.Provider>
  );
};

export const useUI=()=> useContext(UIContext);